/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import { useCallback, useEffect, useState } from "react";
import { Bot, CheckCircle2, ChevronDown, ChevronUp, ShieldAlert, Sparkles, XCircle } from "lucide-react";
import {
  aiErrorMessage, analyzeIncidentWithAi, approveGuardianRepair, fetchAiAudit, fetchAiStatus, fetchGuardianRepairs, fetchIncidentAi, fetchIncidentAiEvidence,
  rejectGuardianRepair, resolveGuardianRepair, updateAiSettings, verifyGuardianRepair,
} from "@/lib/aiApi";

export const AI_SETTINGS_CHANGED = "mysitecare:ai-settings-changed";

// The backend decides every one of these; the UI only explains them in plain language.
export const MODE_OPTIONS = [
  { value: "advisory_only", label: "Advisory only", help: "The Guardian explains what it found and suggests a fix. It never changes anything." },
  { value: "approval_required", label: "Approval required", help: "The Guardian proposes a fix and waits for you to approve it before anything changes." },
  { value: "safe_auto_repair", label: "Safe auto-repair", help: "Only pre-approved, low-risk fixes run on their own. Everything else still waits for you." },
];

export const RISK_LABEL = {
  safe_auto: { text: "Safe to run automatically", className: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  approval_required: { text: "Approval Required", className: "bg-amber-100 text-amber-800 border-amber-300" },
  never_automatic: { text: "Not allowed automatically", className: "bg-rose-100 text-rose-800 border-rose-300" },
};

export const STATUS_LABEL = {
  advisory_only: { text: "Advice only", className: "bg-slate-100 text-slate-700 border-slate-300" },
  awaiting_approval: { text: "Waiting for your decision", className: "bg-amber-100 text-amber-800 border-amber-300" },
  blocked: { text: "Blocked by safety rules", className: "bg-rose-100 text-rose-800 border-rose-300" },
  rejected: { text: "Rejected", className: "bg-slate-100 text-slate-700 border-slate-300" },
  executing: { text: "Running…", className: "bg-sky-100 text-sky-800 border-sky-300" },
  awaiting_verification: { text: "Done — please check it worked", className: "bg-sky-100 text-sky-800 border-sky-300" },
  resolved: { text: "Fixed and verified", className: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  verification_failed: { text: "Check failed — manual recovery needed", className: "bg-rose-100 text-rose-800 border-rose-300" },
  rolled_back: { text: "Check failed — changes undone", className: "bg-orange-100 text-orange-800 border-orange-300" },
  rollback_failed: { text: "Undo failed — manual recovery needed", className: "bg-rose-100 text-rose-800 border-rose-300" },
  approved_manual_apply: { text: "Approved — a person must apply it", className: "bg-sky-100 text-sky-800 border-sky-300" },
  failed: { text: "Could not run", className: "bg-rose-100 text-rose-800 border-rose-300" },
};

const SEVERITY_STYLE = {
  critical: "bg-rose-100 text-rose-800 border-rose-300",
  high: "bg-orange-100 text-orange-800 border-orange-300",
  medium: "bg-amber-100 text-amber-800 border-amber-300",
  low: "bg-slate-100 text-slate-600 border-slate-300",
  info: "bg-slate-100 text-slate-600 border-slate-300",
};

const NEEDS_FOLLOW_UP = ["awaiting_verification", "approved_manual_apply", "verification_failed", "rolled_back", "rollback_failed"];

function Badge({ children, className = "", ...rest }) {
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${className}`} {...rest}>{children}</span>;
}

function formatWhen(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

export function describeProposal(repair) {
  const p = repair.proposal || {};
  if (p.kind === "recovery_action") return `Run the built-in safe recovery “${p.action_id}”.`;
  if (p.kind === "site_capability") return `Turn “${p.capability}” ${p.desired_value ? "on" : "off"} for this website.`;
  if (p.kind === "source_patch") return `Change ${(p.files || []).length} file(s): ${(p.files || []).map((f) => f.file_path).join(", ")}. A person must apply and release it — MySiteCare cannot do that by itself.`;
  return "No change is proposed — this is advice only.";
}

// ---------------------------------------------------------------------------------------------------------------
export function AiRepairCard({ repair, token, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [evidence, setEvidence] = useState(null);
  const [view, setView] = useState(null); // "evidence" | "proposal" | "diff"
  const [confirming, setConfirming] = useState(false);
  const diagnosis = repair.diagnosis || {};
  const risk = RISK_LABEL[repair.classification?.level] || RISK_LABEL.approval_required;
  const status = STATUS_LABEL[repair.status] || { text: repair.status, className: "bg-slate-100 text-slate-700 border-slate-300" };
  const confidencePct = typeof repair.ai_confidence === "number" ? Math.round(repair.ai_confidence * 100) : null;
  const decidable = repair.status === "awaiting_approval";
  const dismissible = decidable || repair.status === "advisory_only";

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      setConfirming(false);
      onChanged && onChanged();
    } catch (err) {
      setError(aiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleView = async (name) => {
    if (view === name) { setView(null); return; }
    if (name === "evidence" && !evidence) {
      try { setEvidence(await fetchIncidentAiEvidence(token, repair.incident_id)); } catch (err) { setError(aiErrorMessage(err)); return; }
    }
    setView(name);
  };

  const btn = "inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50";

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="ai-repair-card">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="font-semibold text-slate-900" data-testid="ai-issue-title">{diagnosis.issue_title || repair.incident?.title || "AI-proposed repair"}</h4>
          {repair.site_slug && <p className="text-xs text-slate-400">Website: <span className="font-mono">{repair.site_slug}</span></p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {diagnosis.severity && <Badge className={SEVERITY_STYLE[diagnosis.severity] || SEVERITY_STYLE.info}>Severity: {diagnosis.severity}</Badge>}
          <Badge className={status.className} data-testid="ai-repair-status">{status.text}</Badge>
        </div>
      </div>

      <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        {diagnosis.likely_cause && <div><dt className="text-xs uppercase tracking-wide text-slate-400">Likely cause</dt><dd className="text-slate-800">{diagnosis.likely_cause}</dd></div>}
        {diagnosis.customer_impact && <div><dt className="text-xs uppercase tracking-wide text-slate-400">Effect on visitors</dt><dd className="text-slate-800">{diagnosis.customer_impact}</dd></div>}
        {confidencePct !== null && <div><dt className="text-xs uppercase tracking-wide text-slate-400">AI confidence</dt><dd className="text-slate-800" data-testid="ai-confidence">{confidencePct}%</dd></div>}
        <div><dt className="text-xs uppercase tracking-wide text-slate-400">Risk</dt><dd><Badge className={risk.className} data-testid="ai-risk">{risk.text}</Badge></dd></div>
        {diagnosis.recommended_action && <div className="sm:col-span-2"><dt className="text-xs uppercase tracking-wide text-slate-400">Recommended action</dt><dd className="text-slate-800">{diagnosis.recommended_action}</dd></div>}
      </dl>

      {repair.status === "advisory_only" && <p className="mt-3 rounded-lg bg-slate-50 p-2 text-xs text-slate-600">The Guardian is in Advisory only mode, so this suggestion will not be applied.</p>}
      {repair.status === "blocked" && (
        <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800" data-testid="ai-blocked-reason">
          The safety rules do not allow the Guardian to do this: {(repair.classification?.reasons || []).join(" ") || "it is outside what can be automated."} A person has to handle it.
        </p>
      )}
      {["verification_failed", "rollback_failed"].includes(repair.status) && (
        <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-2 text-xs font-semibold text-rose-800" data-testid="ai-manual-recovery">Requires manual recovery. {repair.rollback?.detail}</p>
      )}
      {repair.status === "approved_manual_apply" && <p className="mt-3 rounded-lg bg-sky-50 p-2 text-xs text-sky-900">{repair.execution?.detail}</p>}
      {repair.status === "rolled_back" && <p className="mt-3 rounded-lg bg-orange-50 p-2 text-xs text-orange-900">The repair did not pass its check, so the previous state was restored automatically.</p>}
      {repair.verification?.detail && <p className="mt-2 text-xs text-slate-500">Check result: {repair.verification.detail}</p>}

      <div className="mt-3 flex flex-wrap gap-2">
        <button className={btn} onClick={() => toggleView("evidence")} data-testid="ai-view-evidence">View Evidence</button>
        <button className={btn} onClick={() => toggleView("proposal")} data-testid="ai-view-proposal">View Proposed Repair</button>
        {repair.diff && <button className={btn} onClick={() => toggleView("diff")} data-testid="ai-view-diff">View Diff</button>}
      </div>

      {view === "evidence" && evidence && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs" data-testid="ai-evidence">
          <p className="text-slate-600">This is exactly what the AI was shown — passwords, keys and personal identifiers are removed before anything is sent
            ({evidence.redaction_report?.total_redactions ?? 0} item(s) removed).</p>
          <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-slate-700">{JSON.stringify(evidence.evidence, null, 2)}</pre>
        </div>
      )}
      {view === "proposal" && (
        <div className="mt-3 space-y-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700" data-testid="ai-proposal">
          <p><strong>What would change:</strong> {describeProposal(repair)}</p>
          {diagnosis.proposed_verification && <p><strong>How it will be checked:</strong> {diagnosis.proposed_verification}</p>}
          <p><strong>If the check fails:</strong> {repair.classification?.supports_rollback ? (diagnosis.rollback_approach || "The previous state is restored automatically.") : "This type of change cannot be undone automatically — it requires manual recovery."}</p>
          <p className="text-slate-400">Proposed by {repair.provider} ({repair.model}). The safety rules, not the AI, decided the risk level above.</p>
        </div>
      )}
      {view === "diff" && repair.diff && (
        <pre className="mt-3 max-h-64 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-3 font-mono text-xs text-slate-700" data-testid="ai-diff">{repair.diff}</pre>
      )}

      {error && <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800" role="alert" data-testid="ai-error">{error}</p>}

      <div className="mt-3 flex flex-wrap gap-2">
        {decidable && !confirming && (
          <button className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50" disabled={busy} onClick={() => setConfirming(true)} data-testid="ai-approve">
            Approve Repair
          </button>
        )}
        {decidable && confirming && (
          <>
            <span className="self-center text-xs text-slate-600">Apply this change to the website now?</span>
            <button className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50" disabled={busy} onClick={() => run(() => approveGuardianRepair(token, repair.id))} data-testid="ai-approve-confirm">
              {busy ? "Working…" : "Yes, approve"}
            </button>
            <button className={btn} disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
          </>
        )}
        {dismissible && !confirming && (
          <button className={btn} disabled={busy} onClick={() => run(() => rejectGuardianRepair(token, repair.id, "Rejected from the AI Website Guardian panel."))} data-testid="ai-reject">Reject</button>
        )}
        {["awaiting_verification", "verification_failed"].includes(repair.status) && (
          <button className={btn} disabled={busy} onClick={() => run(() => verifyGuardianRepair(token, repair.id))} data-testid="ai-verify">Re-run check</button>
        )}
        {NEEDS_FOLLOW_UP.includes(repair.status) && (
          <button className={btn} disabled={busy} onClick={() => run(() => resolveGuardianRepair(token, repair.id, "Confirmed by an administrator."))} data-testid="ai-mark-resolved">Mark Resolved</button>
        )}
      </div>

      {repair.history?.length > 0 && (
        <details className="mt-3 text-xs text-slate-500">
          <summary className="cursor-pointer font-semibold text-slate-600">History ({repair.history.length})</summary>
          <ul className="mt-1 space-y-0.5">
            {repair.history.map((h, i) => <li key={i}>{formatWhen(h.at)} — {h.actor}: {h.detail}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
const ANALYZE_MESSAGES = {
  disabled: "AI Website Guardian is turned off. Turn it on in the AI Website Guardian card above.",
  provider_unavailable: "AI analysis temporarily unavailable",
  not_found: "That incident no longer exists.",
};

/** Shown inside an incident: ask for an AI analysis, then review/approve it. */
export function AiIncidentAssist({ incident, token, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  const load = useCallback(() => {
    fetchIncidentAi(token, incident.id).then(setData).catch(() => setData({ diagnosis: null, repairs: [] }));
  }, [token, incident.id]);
  useEffect(() => { load(); }, [load]);

  const analyze = async () => {
    setBusy(true);
    setNotice("");
    try {
      const result = await analyzeIncidentWithAi(token, incident.id);
      if (!["analyzed", "cached"].includes(result.status)) setNotice(result.message || ANALYZE_MESSAGES[result.status] || "The AI could not analyse this incident right now.");
      load();
    } catch (err) {
      setNotice(aiErrorMessage(err, "AI analysis temporarily unavailable"));
    } finally {
      setBusy(false);
    }
  };

  const refresh = () => { load(); onChanged && onChanged(); };
  const diagnosis = data?.diagnosis?.diagnosis;
  const repair = data?.repairs?.[0];

  return (
    <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50/40 p-3" data-testid="ai-guardian-incident">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-violet-800"><Bot size={13} /> AI Website Guardian</p>
        {!diagnosis && (
          <button onClick={analyze} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-violet-300 bg-white px-3 py-1.5 text-xs font-semibold text-violet-800 hover:bg-violet-50 disabled:opacity-50" data-testid="ai-analyze">
            <Sparkles size={13} /> {busy ? "Analysing…" : "Analyze with AI"}
          </button>
        )}
      </div>
      {notice && <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" role="status" data-testid="ai-notice">{notice}</p>}
      {!diagnosis && !notice && <p className="mt-2 text-xs text-slate-500">No AI analysis yet. The usual monitoring checks keep running either way.</p>}
      {diagnosis && !repair && (
        <div className="mt-2 text-sm text-slate-700" data-testid="ai-advice-only">
          <p className="font-semibold">{diagnosis.issue_title}</p>
          <p className="mt-1"><strong>Likely cause:</strong> {diagnosis.likely_cause}</p>
          <p className="mt-1"><strong>Suggested next step:</strong> {diagnosis.recommended_action}</p>
          <p className="mt-1 text-xs text-slate-500">AI confidence {Math.round(diagnosis.confidence * 100)}% · No automatic repair is proposed for this incident.</p>
          <button onClick={analyze} disabled={busy} className="mt-2 text-xs font-semibold text-violet-800 hover:underline disabled:opacity-50">{busy ? "Analysing…" : "Analyse again"}</button>
        </div>
      )}
      {repair && <div className="mt-2"><AiRepairCard repair={{ ...repair, diagnosis }} token={token} onChanged={refresh} /></div>}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
function Stat({ label, value, tone = "" }) {
  return (
    <div>
      <p className={`text-2xl font-bold font-display ${tone}`}>{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}

export default function AiGuardianPanel({ token }) {
  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!token) return;
    fetchAiStatus(token).then(setStatus).catch((err) => setError(aiErrorMessage(err, "The AI status could not be loaded.")));
    fetchGuardianRepairs(token, "awaiting_approval").then(setPending).catch(() => setPending([]));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const toggleActivity = async () => {
    if (!activityOpen && !activity) setActivity(await fetchAiAudit(token, 10).catch(() => []));
    setActivityOpen((v) => !v);
  };

  const change = async (patch) => {
    setBusy(true);
    setError("");
    try {
      await updateAiSettings(token, patch);
      load();
      // The Support Copilot card reads the same settings; tell it to refresh instead of keeping a stale "Off".
      window.dispatchEvent(new Event(AI_SETTINGS_CHANGED));
    } catch (err) {
      setError(aiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const mode = MODE_OPTIONS.find((m) => m.value === status?.mode);

  return (
    <section className="mt-4 rounded-xl border border-violet-200 bg-white p-5" data-testid="ai-guardian-panel" aria-labelledby="ai-guardian-heading">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 id="ai-guardian-heading" className="flex items-center gap-2 font-semibold"><Bot size={18} className="text-violet-600" /> AI Website Guardian</h3>
          <p className="mt-1 max-w-prose text-xs text-slate-500">
            Helps explain website problems and proposes fixes. Your normal monitoring keeps running on its own; the AI only looks at real problems, and nothing risky ever happens without you.
          </p>
        </div>
        {status && (
          <div className="flex items-center gap-2">
            <Badge className={status.enabled ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-slate-100 text-slate-600 border-slate-300"} data-testid="ai-enabled-badge">
              {status.enabled ? "Enabled" : "Disabled"}
            </Badge>
            <button
              onClick={() => change({ guardian_enabled: !status.enabled })}
              disabled={busy}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              data-testid="ai-toggle-guardian"
            >
              {status.enabled ? "Turn off" : "Turn on"}
            </button>
          </div>
        )}
      </div>

      {error && <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800" role="alert" data-testid="ai-panel-error">{error}</p>}
      {!status && !error && <p className="mt-3 text-sm text-slate-500">Loading…</p>}

      {status && (
        <>
          <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
            <div><dt className="text-xs uppercase tracking-wide text-slate-400">AI provider</dt><dd data-testid="ai-provider-target">{status.provider?.target}</dd></div>
            <div><dt className="text-xs uppercase tracking-wide text-slate-400">Model</dt><dd className="font-mono text-xs" data-testid="ai-model">{status.provider?.model}</dd></div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-400"><label htmlFor="ai-mode-select">Guardian mode</label></dt>
              <dd>
                <select
                  id="ai-mode-select" value={status.mode} disabled={busy} onChange={(e) => change({ guardian_mode: e.target.value })}
                  className="mt-0.5 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm" data-testid="ai-mode-select"
                >
                  {MODE_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </dd>
            </div>
          </dl>
          {mode && <p className="mt-2 text-xs text-slate-500" data-testid="ai-mode-help">{mode.help}</p>}
          {!status.provider?.configured && (
            <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid="ai-provider-missing">
              The AI service is not connected yet. Monitoring, recovery and your customers’ sites are not affected; AI analysis simply shows “AI analysis temporarily unavailable” until it is connected.
            </p>
          )}

          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="ai-stats">
            <Stat label="Open incidents" value={status.open_incidents} />
            <Stat label="Waiting for approval" value={status.awaiting_approval} tone={status.awaiting_approval > 0 ? "text-amber-600" : ""} />
            <Stat label="Failed repairs" value={status.failed_repairs} tone={status.failed_repairs > 0 ? "text-rose-600" : ""} />
            <Stat label="Rolled-back repairs" value={status.rolled_back_repairs} tone={status.rolled_back_repairs > 0 ? "text-orange-600" : ""} />
            <div><p className="text-sm font-semibold" data-testid="ai-last-analysis">{formatWhen(status.last_analysis?.created_at)}</p><p className="text-xs text-slate-500">Last AI analysis</p></div>
            <div><p className="text-sm font-semibold" data-testid="ai-last-repair">{formatWhen(status.last_successful_repair?.updated_at)}</p><p className="text-xs text-slate-500">Last successful repair</p></div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
            <div className="text-sm">
              <span className="font-semibold">AI Support Copilot:</span>{" "}
              <span data-testid="ai-support-state">{status.support_enabled ? "On — drafts replies for you to review" : "Off"}</span>
            </div>
            <button onClick={() => change({ support_enabled: !status.support_enabled })} disabled={busy} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid="ai-toggle-support">
              {status.support_enabled ? "Turn off" : "Turn on"}
            </button>
          </div>
          <p className="mt-1 text-xs text-slate-400">{status.ai_calls_last_24h} AI call(s) in the last 24 hours. Limits are enforced by the server.</p>

          <h4 className="mt-5 flex items-center gap-1.5 text-sm font-semibold"><ShieldAlert size={15} className="text-amber-600" /> Waiting for your decision</h4>
          <div className="mt-2 space-y-3" data-testid="ai-pending-list">
            {!pending ? <p className="text-sm text-slate-500">Loading…</p> : pending.length === 0 ? (
              <p className="flex items-center gap-1.5 text-sm text-slate-500"><CheckCircle2 size={14} className="text-emerald-600" /> Nothing is waiting for approval.</p>
            ) : pending.map((r) => <AiRepairCard key={r.id} repair={r} token={token} onChanged={load} />)}
          </div>

          <button onClick={toggleActivity} className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800" data-testid="ai-activity-toggle">
            {activityOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Recent AI activity
          </button>
          {activityOpen && (
            <ul className="mt-2 space-y-1 text-xs text-slate-600" data-testid="ai-activity-list">
              {(activity || []).length === 0 ? <li>No AI activity yet.</li> : activity.map((e) => (
                <li key={e.id}>{formatWhen(e.at)} · {String(e.event).replace(/_/g, " ")} · {e.actor}{e.website ? ` · ${e.website}` : ""} {e.result ? `· ${e.result}` : ""}</li>
              ))}
            </ul>
          )}
        </>
      )}
      {status && !status.enabled && <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-400"><XCircle size={12} /> While disabled, the AI is never contacted.</p>}
    </section>
  );
}
